import { FC, useMemo } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { SellerMonthlyAccountedItem } from '@shared/api/needAccount/needAccount.interface';
import { useAuth } from '@shared/state/hooks/useAuth';
import { getAccountDate } from '@shared/utils/dateUtil';
import { formatDate } from '@shared/utils/formatDate';
import { DatePicker, Table, TableColumnsType } from 'antd';
import locale from 'antd/es/date-picker/locale/ko_KR';
import _ from 'lodash';

import { SellerAccountDetailModalButton } from '../SellerAccountDetailModal/SellerAccountDetailModalButton';
import {
  SELLER_BRAND_ETC_ID,
  sellerAccountSelectYearAtom,
  sellerBrandOptionsAtom,
  sellerSelectedBrandIdsAtom,
} from './accountContent.atom';
import { RebateButton } from './RebateButton';

//* 연간 정산 완료 및 예정 내역
interface Props {
  monthlyAccounted: SellerMonthlyAccountedItem[];
  selectedYear: number;
}

type AccountData = {
  id: string;
  month: number;
  accountDate?: Date;
  accountCount: number; // 정산건수
  salesCount: number; // 판매건수
  rebatedCount: number; // 상조정산 완료 건수
  accountPrice: number;
  rebatePrice: number;
};

const columns: TableColumnsType<AccountData> = [
  {
    title: '정산기간',
    dataIndex: 'month',
    key: 'month',
    align: 'center',
    render: (month: number) => {
      return <p className="px-6 py-1 text-base font-medium">{month}월</p>;
    },
  },
  {
    title: '정산일',
    dataIndex: 'accountDate',
    key: 'accountDate',
    align: 'center',
    render: (_, record) => {
      if (record.accountDate) {
        return (
          <p className="px-6 py-1 text-base font-medium text-green-600">
            {formatDate(record.accountDate, {
              contains: {
                year: false,
              },
            })}{' '}
            완료
          </p>
        );
      } else {
        const accountDate = getAccountDate(record.month);
        const now = new Date();
        if (now.getTime() > accountDate.getTime()) {
          return (
            <p className="px-6 py-1 text-base font-medium theme-text-warn-03">
              {formatDate(getAccountDate(record.month), {
                contains: {
                  year: false,
                },
              })}{' '}
              미지급
            </p>
          );
        } else {
          return (
            <p className={`px-6 py-1 text-base font-medium`}>
              {formatDate(getAccountDate(record.month), {
                contains: {
                  year: false,
                },
              })}{' '}
              예정
            </p>
          );
        }
      }
    },
  },
  {
    title: '총 판매건수',
    dataIndex: 'accountCount',
    key: 'accountCount',
    align: 'center',
    render: (accountCount: number) => {
      return (
        <p className="px-6 py-1 text-base">
          {accountCount === 0 ? '-' : accountCount.toLocaleString() + '개'}
        </p>
      );
    },
  },
  {
    title: '화환 정산금',
    dataIndex: 'accountPrice',
    key: 'accountPrice',
    align: 'center',
    render: (accountPrice: number) => {
      return (
        <p className="px-6 py-1 text-base font-medium">
          {accountPrice === 0 ? '-' : accountPrice.toLocaleString() + '원'}
        </p>
      );
    },
  },
  {
    title: '상조 정산금',
    dataIndex: 'rebatePrice',
    key: 'rebatePrice',
    align: 'center',
    render: (rebatePrice: number) => {
      return (
        <p className="px-6 py-1 text-base font-medium">
          {rebatePrice === 0 ? '-' : rebatePrice.toLocaleString() + '원'}
        </p>
      );
    },
  },
  {
    title: '합계',
    key: 'total',
    align: 'center',
    render: (_, record) => {
      return (
        <p className="px-6 py-1 text-base font-medium">
          {record.accountPrice + record.rebatePrice === 0
            ? '-'
            : (record.accountPrice + record.rebatePrice).toLocaleString() + '원'}
        </p>
      );
    },
  },
  {
    title: '세부내역보기',
    key: 'detail',
    align: 'center',
    render: (_, record) => {
      return (
        <div className="center-box">
          <SellerAccountDetailModalButton
            year={new Date().getFullYear()}
            month={record.month}
            type="accounted"
          />
        </div>
      );
    },
  },
];

export const AnnualAccountedContent: FC<Props> = function AnnualAccountedContent({
  monthlyAccounted,
  selectedYear,
}: Props) {
  const { isAdmin } = useAuth();
  const [selectYear, setSelectYear] = useRecoilState(sellerAccountSelectYearAtom);

  // BugoBrand Filtering (ID 기반)
  const brandOptions = useRecoilValue(sellerBrandOptionsAtom);
  const selectedBrandIds = useRecoilValue(sellerSelectedBrandIdsAtom);

  // etc 판별용: 실제 등록 브랜드 ID 목록 (etc 제외)
  const knownBrandIds = useMemo(
    () =>
      new Set(brandOptions.filter((o) => o.id !== SELLER_BRAND_ETC_ID).map((o) => o.id)),
    [brandOptions],
  );

  // 월별 데이터의 brands 배열 내에서 선택된 bugoBrand만 필터 후 재집계
  const tableData: AccountData[] = useMemo(() => {
    const isEtcSelected = selectedBrandIds.has(SELLER_BRAND_ETC_ID);
    return monthlyAccounted.map((monthItem) => {
      const filteredBrands = monthItem.brands.filter((b) => {
        const isKnown = knownBrandIds.has(b.bugoBrandId);
        return isKnown ? selectedBrandIds.has(b.bugoBrandId) : isEtcSelected;
      });
      return {
        id: `accountTable-${monthItem.month}`,
        month: monthItem.month,
        accountDate: monthItem.accountedDate
          ? new Date(monthItem.accountedDate)
          : undefined,
        accountCount: _.sumBy(filteredBrands, 'totalCount'),
        salesCount: _.sumBy(filteredBrands, 'salesCount'),
        rebatedCount: _.sumBy(filteredBrands, 'rebatedCount'),
        accountPrice: _.sumBy(filteredBrands, 'totalPriceWhole'),
        rebatePrice: _.sumBy(filteredBrands, 'totalPriceRebate'),
      };
    });
  }, [monthlyAccounted, selectedBrandIds, knownBrandIds]);

  // 필터된 합산
  const filteredSummary = useMemo(() => {
    const isEtcSelected = selectedBrandIds.has(SELLER_BRAND_ETC_ID);
    const allFilteredBrands = monthlyAccounted.flatMap((m) =>
      m.brands.filter((b) => {
        const isKnown = knownBrandIds.has(b.bugoBrandId);
        return isKnown ? selectedBrandIds.has(b.bugoBrandId) : isEtcSelected;
      }),
    );
    return {
      totalCount: _.sumBy(allFilteredBrands, 'totalCount'),
      salesCount: _.sumBy(allFilteredBrands, 'salesCount'),
      totalPriceWhole: _.sumBy(allFilteredBrands, 'totalPriceWhole'),
      totalPriceRebate: _.sumBy(allFilteredBrands, 'totalPriceRebate'),
    };
  }, [monthlyAccounted, selectedBrandIds, knownBrandIds]);

  // columns에서 selectedYear를 사용하도록 year를 주입
  const columnsWithYear: TableColumnsType<AccountData> = useMemo(() => {
    return columns.map((col) => {
      if (col.key === 'detail') {
        return {
          ...col,
          render: (_: unknown, record: AccountData) => (
            <div className="center-box">
              <SellerAccountDetailModalButton
                year={selectedYear}
                month={record.month}
                type="accounted"
              />
            </div>
          ),
        };
      }
      return col;
    });
  }, [selectedYear]);

  // admin일 때만 상조정산 컬럼 추가
  const finalColumnsWithYear: TableColumnsType<AccountData> = useMemo(() => {
    if (!isAdmin()) return columnsWithYear;
    return [
      ...columnsWithYear,
      {
        title: '상조정산',
        key: 'rebate',
        align: 'center' as const,
        render: (_: unknown, record: AccountData) => {
          const total = record.accountCount;
          const done = record.rebatedCount ?? 0;
          const allDone = total > 0 && done >= total;
          return (
            <RebateButton year={selectedYear} month={record.month}>
              <span
                className={`text-sm font-medium hover:underline ${
                  allDone
                    ? 'text-green-600'
                    : done > 0
                    ? 'text-orange-500'
                    : 'text-gray-500'
                }`}
              >
                {done}/{total}
              </span>
            </RebateButton>
          );
        },
      },
    ];
  }, [isAdmin, columnsWithYear, selectedYear]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between pr-4">
        <p className="pl-4 text-base font-medium">
          {selectYear?.format('YYYY년')} 연간 정산완료 내역
        </p>
        <DatePicker
          picker="year"
          locale={locale}
          size="large"
          defaultValue={selectYear ?? undefined}
          onChange={(date) => {
            setSelectYear(date);
          }}
        />
      </div>
      <Table
        className="w-full"
        columns={finalColumnsWithYear}
        dataSource={_.flatMapDeep([tableData])}
        pagination={{
          defaultPageSize: 13,
          hideOnSinglePage: true,
        }}
        rowKey={'id'}
        bordered
        size={'small'}
      />
      <div className="button-rectangle flex cursor-default items-center justify-evenly gap-4 text-base theme-bg-9">
        <p>합산</p>
        <p>{filteredSummary.totalCount}건</p>
        <p>{(filteredSummary.totalPriceWhole ?? 0).toLocaleString()}원</p>
        <p>{(filteredSummary.totalPriceRebate ?? 0).toLocaleString()}원</p>
        <p>
          {(
            (filteredSummary.totalPriceWhole ?? 0) +
            (filteredSummary.totalPriceRebate ?? 0)
          ).toLocaleString()}
          원
        </p>
      </div>
    </div>
  );
};
